import SEO from "components/seo";
import LikedContainer from "containers/liked/liked";
import { useInfiniteQuery, useQuery } from "react-query";
import likeService from "services/like";
import { useAuth } from "contexts/auth/auth.context";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import useDebounce from "hooks/useDebounce";
import { useAppSelector } from "hooks/useRedux";
import shopService from "services/shop";
import qs from "qs";
import SettingsContainer from "containers/settings/settings";

export default function LikedShopsPage() {
  const { isAuthenticated } = useAuth();
  const { i18n } = useTranslation();
  const { shopList } = useAppSelector((state) => state.liked);

  const lang = i18n.language;
  const debouncedShopList = useDebounce(shopList, 500);

  const {
    data: serverLikedShops,
    isLoading: isServerLikedShopsLoading,
    isFetching: isServerLikedShopsFetching,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ["likedShops", lang],
    queryFn: ({ pageParam = 1 }) =>
      likeService.getAll({
        type: "shop",
        perPage: 12,
        page: pageParam,
      }),
    getNextPageParam: (lastPage: any) => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page) {
        return lastPage?.meta?.current_page + 1;
      }
    },
    enabled: isAuthenticated,
    staleTime: 0,
  });

  const { data: localLikedShops, isLoading: isLocalLikedShopsLoading } =
    useQuery({
      queryKey: ["likedUnAuthShops", lang, debouncedShopList],
      queryFn: () => {
        const params = {
          perPage: 100,
          ...debouncedShopList.reduce(
            (acc: any, curr: any, index: number) => ({
              ...acc,
              [`ids[${index}]`]: curr.shopId,
            }),
            {},
          ),
        };
        return shopService.getAll(qs.stringify(params));
      },
      enabled: !isAuthenticated && !!debouncedShopList.length,
      keepPreviousData: true,
      staleTime: 0,
    });

  const extractedServerLikedShops = serverLikedShops?.pages?.flatMap(
    (page) => page?.data || [],
  );

  const actualList = useMemo(() => {
    if (isAuthenticated) {
      return extractedServerLikedShops || [];
    }
    if (shopList.length) {
      return localLikedShops?.data || [];
    }
    return [];
  }, [isAuthenticated, extractedServerLikedShops, localLikedShops, shopList]);

  return (
    <>
      <SEO />
      <SettingsContainer>
        <LikedContainer
          type="shop"
          data={actualList}
          isLoading={
            isAuthenticated
              ? isServerLikedShopsLoading && isServerLikedShopsFetching
              : isLocalLikedShopsLoading
          }
          hasNextPage={hasNextPage}
          isFetchingNextPage={isServerLikedShopsFetching}
          fetchingNextPage={fetchNextPage}
        />
      </SettingsContainer>
    </>
  );
}
